import type { SubscriptionStatusResponse } from "@/clients/models/SubscriptionStatusResponse";
import type { SubscriptionPurchaseBackdropState } from "@/components/subscription/subscription-purchase-backdrop";
import { QueryKeys } from "@/constants/query-keys";
import { useToast } from "@/hooks/ui/use-toast";
import { useIsSubscriptionEnabled } from "@/hooks/use-is-subscription-enabled";
import { isPurchaseCancelledByUser, purchasePackage } from "@/utils/revenue-cat";
import { verifyWithBackoff } from "@/utils/subscription/verify-with-backoff";
import { createPurchaseAttempt, type PurchasePlacement } from "../../analytics";
import type { PurchasesPackage } from "@revenuecat/purchases-capacitor";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

interface UsePurchaseAndVerifyOptions {
  placement: PurchasePlacement;
  /**
   * Caller-specific side effects after the subscription is confirmed active —
   * navigation, extra cache invalidation, closing a modal, etc. The hook has
   * already written `[SUBSCRIPTION_STATUS]` and closed the backdrop by the time
   * this runs. May be async; the hook awaits it.
   */
  onUnlocked: (status: SubscriptionStatusResponse) => void | Promise<void>;
  /** Console log prefix, e.g. "[Paywall]" / "[SubscriptionUpgrade]". */
  logPrefix: string;
}

interface UsePurchaseAndVerifyResult {
  isPurchasing: boolean;
  isBackdropOpen: boolean;
  backdropState: SubscriptionPurchaseBackdropState;
  /** Run the RevenueCat purchase → verify → unlock flow for the given package. */
  startPurchase: (selectedPackage: PurchasesPackage | undefined) => Promise<void>;
  handleManualRetry: () => Promise<void>;
  handleBackdropCancel: () => void;
}

/**
 * Shared purchase → verify → unlock state machine for the paywall and upgrade
 * flows. Extracted from the three near-identical copies that previously lived in
 * `free-trial-paywall`, `expired-trial-paywall`, and `subscription-upgrade`.
 *
 * Flow: `purchasePackage()` (native store sheet) → `verifyWithBackoff()` (single
 * backend verify, backoff on 502). On `is_active` it writes `[SUBSCRIPTION_STATUS]`,
 * closes the backdrop, and calls `onUnlocked(fresh)`. On not-active it shows the
 * manual-retry backdrop (no auto-poll, per the backend contract). User-cancelled
 * purchases are silent; all other failures surface a toast.
 */
export const usePurchaseAndVerify = ({
  onUnlocked,
  logPrefix,
  placement,
}: UsePurchaseAndVerifyOptions): UsePurchaseAndVerifyResult => {
  const queryClient = useQueryClient();
  const { showError } = useToast();
  const { isEnabled: isSubscriptionEnabled } = useIsSubscriptionEnabled();

  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isBackdropOpen, setIsBackdropOpen] = useState(false);
  const [backdropState, setBackdropState] =
    useState<SubscriptionPurchaseBackdropState>("processing");

  const isMountedRef = useRef(true);
  const attemptRef = useRef<ReturnType<typeof createPurchaseAttempt> | null>(null);
  const purchaseInFlight = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const runVerify = useCallback(async () => {
    const attempt = attemptRef.current;
    let verificationReturned = false;
    try {
      const fresh = await verifyWithBackoff();
      verificationReturned = true;
      attempt?.verification(fresh.is_active ? "active" : "inactive");
      if (!isMountedRef.current) return;
      if (fresh.is_active) {
        queryClient.setQueryData([QueryKeys.SUBSCRIPTION_STATUS], fresh);
        setIsBackdropOpen(false);
        await onUnlocked(fresh);
      } else {
        setBackdropState("needsManualRetry");
      }
    } catch (error) {
      if (!verificationReturned) attempt?.verification("failed");
      console.error(`${logPrefix} Verify failed`, error);
      if (!isMountedRef.current) return;
      showError("We couldn't confirm your purchase. Please try again.");
      setIsBackdropOpen(false);
    }
  }, [logPrefix, onUnlocked, queryClient, showError]);

  const startPurchase = useCallback(
    async (selectedPackage: PurchasesPackage | undefined) => {
      if (isPurchasing || purchaseInFlight.current || !selectedPackage) return;
      purchaseInFlight.current = true;
      attemptRef.current = createPurchaseAttempt(selectedPackage, placement);

      setIsPurchasing(true);
      setBackdropState("processing");
      setIsBackdropOpen(true);

      try {
        await purchasePackage(selectedPackage, isSubscriptionEnabled);
        attemptRef.current.result("succeeded");
      } catch (error) {
        attemptRef.current.result(isPurchaseCancelledByUser(error) ? "cancelled" : "failed");
        purchaseInFlight.current = false;
        if (!isPurchaseCancelledByUser(error)) {
          console.error(`${logPrefix} Purchase failed`, error);
          showError("Purchase failed. Please try again.");
        }
        if (isMountedRef.current) {
          setIsBackdropOpen(false);
          setIsPurchasing(false);
        }
        return;
      }

      await runVerify();
      purchaseInFlight.current = false;

      if (isMountedRef.current) {
        setIsPurchasing(false);
      }
    },
    [isPurchasing, isSubscriptionEnabled, logPrefix, placement, runVerify, showError],
  );

  const handleManualRetry = useCallback(async () => {
    setBackdropState("processing");
    await runVerify();
  }, [runVerify]);

  const handleBackdropCancel = useCallback(() => {
    setIsBackdropOpen(false);
    setIsPurchasing(false);
  }, []);

  return {
    isPurchasing,
    isBackdropOpen,
    backdropState,
    startPurchase,
    handleManualRetry,
    handleBackdropCancel,
  };
};
